import {
    Form,
    Button,
    Row,
    Col,
    InputGroup,
    FloatingLabel,
    Image,
    Spinner,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { submitFeedback } from '@/shared/services/feedbackApi';
import {
    validateImageFile,
    ACCEPTED_FILE_EXTENSIONS,
} from '@/shared/utils/imageValidation';
import logger from '@/shared/utils/logger';
import Captcha from '@/components/common/Captcha';
import emailIcon from '@/assets/img/icons/svg/emailIcon.svg';

// Category options
const CATEGORIES = [
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'feedback', label: 'General Feedback' },
];

// Page/Feature options
const PAGE_FEATURES = [
    { value: '', label: 'Select a page or feature...' },
    { value: 'Home Page', label: 'Home Page' },
    { value: 'About Page', label: 'About Page' },
    { value: 'Dashboard', label: 'Dashboard' },
    { value: 'Bili Chat', label: 'Bili Chat' },
    { value: 'Expert Query', label: 'Expert Query' },
    { value: 'Communities', label: 'Communities' },
    { value: 'Profile / Account', label: 'Profile / Account' },
    { value: 'Login / Signup', label: 'Login / Signup' },
    { value: 'Contact Form', label: 'Contact Form' },
    { value: 'Navigation', label: 'Navigation' },
    { value: 'Other', label: 'Other' },
];

export default function FeedbackForm({ onSuccess = null }) {
    const { user, profile } = useAuth();
    const fileInputRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        category: '',
        description: '',
        pageFeature: '',
        email: profile?.email || user?.email || '',
        contactRequested: false,
        screenshot: null,
    });

    // State to track which fields have been touched
    const [touched, setTouched] = useState({
        category: false,
        description: false,
        pageFeature: false,
        email: false,
    });

    // Validation errors
    const [errors, setErrors] = useState({});

    // Captcha State
    const [captchaToken, setCaptchaToken] = useState(null);
    const captchaRef = useRef(null);

    // UI state
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [screenshotPreview, setScreenshotPreview] = useState(null);

    /**
     * Validates the form values
     */
    const validate = (values) => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!values.category) {
            newErrors.category = 'Please select a category.';
        }

        if (!values.description.trim()) {
            newErrors.description = 'Description is required.';
        } else if (values.description.length < 10) {
            newErrors.description =
                'Description must be at least 10 characters.';
        }

        if (!values.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!emailRegex.test(values.email)) {
            newErrors.email = 'Please provide a valid email.';
        }

        return newErrors;
    };

    /**
     * Handles input change and validates
     */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        const updatedFormData = { ...formData, [name]: newValue };
        setFormData(updatedFormData);
        setErrors(validate(updatedFormData));
    };

    /**
     * Handles input blur to mark field as touched
     */
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        setErrors(validate(formData));
    };

    /**
     * Handle file selection
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            const validationError = validateImageFile(file);
            if (validationError) {
                setSubmitError(validationError);
                return;
            }

            // Revoke previous object URL to prevent memory leak
            if (screenshotPreview) {
                URL.revokeObjectURL(screenshotPreview);
            }
            setFormData((prev) => ({ ...prev, screenshot: file }));
            setScreenshotPreview(URL.createObjectURL(file));
            setSubmitError('');
        }
    };

    /**
     * Remove screenshot
     */
    const handleRemoveScreenshot = () => {
        // Revoke object URL to prevent memory leak
        if (screenshotPreview) {
            URL.revokeObjectURL(screenshotPreview);
        }
        setFormData((prev) => ({ ...prev, screenshot: null }));
        setScreenshotPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        const newErrors = validate(formData);
        setErrors(newErrors);
        setTouched({
            category: true,
            description: true,
            pageFeature: true,
            email: true,
        });

        if (Object.keys(newErrors).length > 0) {
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setSubmitError('');

        try {
            await submitFeedback({
                ...formData,
                userId: user?.uid,
                isLoggedIn: !!user,
                captchaToken,
            });

            setSubmitSuccess(true);

            // Call onSuccess callback if provided
            if (onSuccess) {
                setTimeout(() => {
                    onSuccess();
                }, 2000);
            }
        } catch (err) {
            logger.error('Error submitting feedback:', err);
            setSubmitError(
                err.message || 'Failed to submit feedback. Please try again.'
            );
        } finally {
            setLoading(false);
            captchaRef.current?.reset();
            setCaptchaToken(null);
        }
    };

    // Show success message
    if (submitSuccess) {
        return (
            <div className="mx-auto text-center" role="status">
                <Row className="mb-3 form-header">
                    <h1 className="form-title text-primary">Thank You!</h1>
                    <div className="form-description h6">
                        Your feedback has been submitted successfully. We
                        appreciate you taking the time to help us improve.
                    </div>
                </Row>
            </div>
        );
    }

    return (
        <Form noValidate onSubmit={handleSubmit} className="mx-auto">
            {/* Header */}
            <Row className="mb-3 form-header">
                <h1 className="form-title text-primary">Send Feedback</h1>
                <div className="form-description h6">
                    Help us improve Sustainability Hub by sharing your thoughts,
                    reporting bugs, or suggesting new features.
                </div>
            </Row>

            {/* Error Message */}
            {submitError && (
                <div className="alert alert-danger text-center" role="alert">
                    {submitError}
                </div>
            )}

            {/* Category Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingCategory"
                            label="Category *"
                        >
                            <Form.Select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                    touched.category && !!errors.category
                                }
                                isValid={
                                    touched.category &&
                                    !errors.category &&
                                    formData.category !== ''
                                }
                                disabled={loading}
                                aria-describedby={
                                    validated && errors.category
                                        ? 'category-error'
                                        : undefined
                                }
                            >
                                <option value="">Select a category...</option>
                                {CATEGORIES.map((cat) => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.category && (
                        <div
                            id="category-error"
                            role="alert"
                            className="invalid-feedback d-block fw-semibold ps-3"
                        >
                            {errors.category}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Page/Feature Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingPageFeature"
                            label="Page / Feature"
                        >
                            <Form.Select
                                name="pageFeature"
                                value={formData.pageFeature}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={loading}
                            >
                                {PAGE_FEATURES.map((pf) => (
                                    <option key={pf.value} value={pf.value}>
                                        {pf.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </FloatingLabel>
                    </InputGroup>
                </Form.Group>
            </Row>

            {/* Description Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <Form.Control
                        as="textarea"
                        name="description"
                        aria-label="Description"
                        placeholder="Please describe your feedback in detail... (minimum 10 characters)"
                        value={formData.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.description && !!errors.description}
                        isValid={
                            touched.description &&
                            !errors.description &&
                            formData.description.trim() !== ''
                        }
                        disabled={loading}
                        className="message-textarea"
                        aria-describedby={
                            validated && errors.description
                                ? 'description-error'
                                : undefined
                        }
                    />
                    {validated && errors.description && (
                        <div
                            id="description-error"
                            role="alert"
                            className="invalid-feedback d-block fw-semibold"
                        >
                            {errors.description}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Screenshot Upload */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <div className="screenshot-upload">
                        <Form.Label
                            htmlFor="feedback-screenshot"
                            className="text-muted mb-2"
                        >
                            Screenshot (Optional)
                        </Form.Label>
                        <Form.Control
                            id="feedback-screenshot"
                            type="file"
                            ref={fileInputRef}
                            accept={ACCEPTED_FILE_EXTENSIONS}
                            onChange={handleFileChange}
                            disabled={loading}
                            className="border-0 p-0"
                        />
                        <Form.Text className="text-muted">
                            Max 5MB. Accepted formats: JPEG, PNG, HEIC
                        </Form.Text>
                        {screenshotPreview && (
                            <div className="mt-3">
                                <Image
                                    src={screenshotPreview}
                                    alt="Screenshot preview"
                                    className="screenshot-preview"
                                />
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="ms-3"
                                    onClick={handleRemoveScreenshot}
                                    aria-label="Remove screenshot"
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                    </div>
                </Form.Group>
            </Row>

            {/* Email Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <img src={emailIcon} alt="" />
                        <FloatingLabel
                            controlId="floatingEmail"
                            label="Email *"
                        >
                            <Form.Control
                                type="email"
                                name="email"
                                placeholder="your@email.com"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.email && !!errors.email}
                                isValid={
                                    touched.email &&
                                    !errors.email &&
                                    formData.email.trim() !== ''
                                }
                                disabled={loading}
                                aria-describedby={
                                    validated && errors.email
                                        ? 'feedback-email-error'
                                        : undefined
                                }
                            />
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.email && (
                        <div
                            id="feedback-email-error"
                            role="alert"
                            className="invalid-feedback d-block fw-semibold ps-3"
                        >
                            {errors.email}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Contact Me Checkbox */}
            <Row className="mb-4">
                <Form.Group as={Col} md="12">
                    <Form.Check
                        type="checkbox"
                        id="contactRequested"
                        name="contactRequested"
                        checked={formData.contactRequested}
                        onChange={handleChange}
                        label="I'd like to be contacted by a team member about this feedback"
                        disabled={loading}
                    />
                </Form.Group>
            </Row>

            {/* Captcha */}
            <Row className="mb-4 justify-content-center">
                <Col xs="auto">
                    <Captcha
                        ref={captchaRef}
                        onVerify={setCaptchaToken}
                        size="normal"
                        theme="light"
                        onExpired={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                    />
                </Col>
            </Row>

            {/* Submit Button */}
            <Button
                className="form-send-btn fw-bold"
                type="submit"
                disabled={loading || !captchaToken}
            >
                {loading ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Submitting...
                    </>
                ) : (
                    'Submit Feedback'
                )}
            </Button>
        </Form>
    );
}

FeedbackForm.propTypes = {
    onSuccess: PropTypes.func,
};
